import config from '../config/environment';

// Interface matching actual backend response from GET /api/admin/stats
export interface PlatformStats {
  users: {
    total: number;
    active: number;
    recentActive: number; // Last 24h
    admins: number;
    superAdmins: number;
  };
  games: {
    total: number;
    active: number;
    recent: number; // Last 24h
  };
  timestamp: string;
}

export interface SystemHealth {
  status: 'healthy' | 'degraded' | 'down';
  database: {
    connected: boolean;
    responseTime?: number;
  };
  redis?: {
    connected: boolean;
    responseTime?: number;
  };
  uptime: number;
  memory: {
    used: number;
    total: number;
    percentage: number;
  };
  environment: string;
  timestamp: string;
}

class AdminApi {
  private baseUrl: string;

  constructor() {
    this.baseUrl = config.api.baseUrl;
  }

  private async fetchWithAuth<T>(endpoint: string, options?: RequestInit): Promise<T> {
    // Use 'authToken' key as defined in AUTH_STORAGE_KEYS, not 'token'
    const token = localStorage.getItem('authToken');
    
    if (!token) {
      console.error('[AdminApi] No access token found in localStorage');
      console.log('[AdminApi] Available localStorage keys:', Object.keys(localStorage));
      throw new Error('Access token required. Please log in again.');
    }
    
    console.log('[AdminApi] Making request to:', `${this.baseUrl}${endpoint}`);
    console.log('[AdminApi] Token exists:', !!token, 'Length:', token?.length);
    
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options?.headers,
      },
    });

    if (!response.ok) {
      console.error('[AdminApi] Request failed:', response.status, response.statusText);
      const error = await response.json().catch(() => ({ message: 'Request failed' }));
      throw new Error(error.message || `HTTP ${response.status}`);
    }

    return response.json();
  }

  async getPlatformStats(): Promise<PlatformStats> {
    const response = await this.fetchWithAuth<{ success: boolean; data: PlatformStats }>(
      '/api/admin/stats'
    );
    return response.data;
  }

  async getSystemHealth(): Promise<SystemHealth> {
    const response = await this.fetchWithAuth<{ success: boolean; data: SystemHealth }>(
      '/api/admin/health'
    );
    return response.data;
  }

  async getUsers(params?: {
    page?: number;
    limit?: number;
    search?: string;
    role?: string;
    isActive?: boolean;
  }) {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.role) queryParams.append('role', params.role);
    if (params?.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());

    return this.fetchWithAuth(`/api/admin/users?${queryParams}`);
  }

  async getUserDetails(userId: string) {
    return this.fetchWithAuth(`/api/admin/users/${userId}`);
  }

  async createUser(data: {
    username: string;
    email: string;
    password: string;
    displayName?: string;
    role?: string;
  }) {
    return this.fetchWithAuth(`/api/admin/users`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateUser(userId: string, data: any) {
    return this.fetchWithAuth(`/api/admin/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteUser(userId: string) {
    return this.fetchWithAuth(`/api/admin/users/${userId}`, {
      method: 'DELETE',
    });
  }

  async toggleUserActive(userId: string) {
    return this.fetchWithAuth(`/api/admin/users/${userId}/toggle-active`, {
      method: 'POST',
    });
  }

  async banUser(userId: string, reason?: string) {
    return this.fetchWithAuth(`/api/admin/users/${userId}/ban`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  async unbanUser(userId: string) {
    return this.fetchWithAuth(`/api/admin/users/${userId}/unban`, {
      method: 'POST',
    });
  }

  // Room Management Methods
  async getRooms(params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    gameType?: string;
  }) {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.gameType) queryParams.append('gameType', params.gameType);

    return this.fetchWithAuth(`/api/admin/rooms?${queryParams}`);
  }

  async getRoomDetails(roomId: string) {
    return this.fetchWithAuth(`/api/admin/rooms/${roomId}`);
  }

  async closeRoom(roomId: string) {
    return this.fetchWithAuth(`/api/admin/rooms/${roomId}/close`, {
      method: 'POST',
    });
  }

  async deleteRoom(roomId: string) {
    return this.fetchWithAuth(`/api/admin/rooms/${roomId}`, {
      method: 'DELETE',
    });
  }

  async sendAnnouncement(data: { title: string; message: string; type?: string }) {
    return this.fetchWithAuth('/api/admin/announcements', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async clearCache() {
    return this.fetchWithAuth('/api/admin/cache/clear', {
      method: 'POST',
    });
  }

  async emergencyStop() {
    return this.fetchWithAuth('/api/admin/emergency-stop', {
      method: 'POST',
    });
  }

  async getSystemLogs(limit = 100) {
    return this.fetchWithAuth(`/api/admin/logs?limit=${limit}`, {
      method: 'GET',
    });
  }

  async getSettings() {
    return this.fetchWithAuth('/api/admin/settings', {
      method: 'GET',
    });
  }

  async updateSettings(settings: any) {
    return this.fetchWithAuth('/api/admin/settings', {
      method: 'PATCH',
      body: JSON.stringify(settings),
    });
  }
}

export const adminApi = new AdminApi();
